import { defineStore } from "pinia";
import { parseISO, isFuture, isPast, isValid } from "date-fns";
import { useFavicon, useStorage } from "@vueuse/core";
import { publicBoaHttp, privateBoaHttp, trpcClient } from "@/boa-api-client";
import { useAuthStore } from ".";
import semver from "semver";
import type { User } from "best-of-awards-api";
import type { Graphic } from "@/views/common/Graphics.vue";
import type {
  Award,
  AwardInstance,
  AwardSettings,
} from "best-of-awards-api/dist/lib/awards/types";

export function isAwardSettings(item: unknown): item is AwardSettings {
  return (
    !!item &&
    typeof item === "object" &&
    "PK" in item &&
    item.PK === "AWARD" &&
    "SK" in item &&
    item.SK === "SETTINGS"
  );
}

export type AnnouncementItem = {
  date: Date | string;
  expiryDate: Date | string;
  text: string;
  type: "ANNOUNCEMENT";
  PK?: string;
  SK?: string;
};

export type CalendarItem = {
  date: Date | string;
  text: string;
  type: "CALENDAR";
  PK?: string;
  SK?: string;
};

const AWARD_STORAGE_KEY = "boa-award";

const emptyAward: Award = {
  id: "",
  instances: [],
  name: "",
  settings: {},
  table: "",
};

const storedAward = useStorage<Award>(AWARD_STORAGE_KEY, emptyAward);

enum AwardTables {
  AtlantasBest = "best-of-awards-atlantas-best",
  BestOfSlc = "best-of-awards-best-of-slc",
  BestOfSlcDev = "best-of-awards-best-of-slc-dev",
  BestOfChicagoDev = "best-of-awards-best-of-chicago-dev",
  BestOfMileHigh = "best-of-awards-best-of-mile-high",
  BestOfMileHighDev = "best-of-awards-best-of-mile-high-dev",
  IdahosBest = "best-of-awards-idahos-best",
  IdahosBestDev = "best-of-awards-idahos-best-dev",
  OrlandosBest = "best-of-awards-orlandos-best",
  SanJosesBest = "best-of-awards-san-joses-best",
  SanJosesBestDev = "best-of-awards-san-joses-best-dev",
}

type State = {
  activeAward: Award;
  awards: Award[];
  awardsLoading: boolean;
  awardSettingsLoading: boolean;
};

export function getDefaultAwardTable() {
  const { hostname } = window.location;
  let dbTable = "";
  switch (hostname) {
    case "localhost": {
      dbTable = AwardTables.BestOfMileHighDev;
      break;
    }
    case "best-of-awards-dashboard-dev.s3-website-us-west-2.amazonaws.com": {
      dbTable = AwardTables.BestOfChicagoDev;
      break;
    }
    case "app.bestofslc.com": {
      dbTable = AwardTables.BestOfSlc;
      break;
    }
    case "app.idahosbest.com": {
      dbTable = AwardTables.IdahosBest;
      break;
    }
    case "app.bestofmilehigh.com": {
      dbTable = AwardTables.BestOfMileHigh;
      break;
    }
    case "app.sanjosesbest.com": {
      dbTable = AwardTables.SanJosesBest;
      break;
    }
    case "app.atlantasbest.com": {
      dbTable = AwardTables.AtlantasBest;
      break;
    }
    case "app.orlandosbest.com": {
      dbTable = AwardTables.OrlandosBest;
      break;
    }
    default: {
      break;
    }
  }
  storedAward.value.table = dbTable;
  return dbTable;
}

export default defineStore("award", {
  state: (): State => {
    const dbTable = getDefaultAwardTable();
    return {
      activeAward: {
        id: "",
        instances: [],
        name: "",
        settings: {},
        table: dbTable,
      },
      awards: [],
      awardsLoading: true,
      awardSettingsLoading: true,
    };
  },
  getters: {
    activeAwardInstance: (state): Partial<AwardInstance> | null => {
      const latestInstance = state.activeAward?.instances[0];
      if (latestInstance?.isDraft) {
        return state.activeAward?.instances[1] ?? null;
      }
      return latestInstance ?? null;
    },
    cutoff(): Date | null {
      if (!this.activeAwardInstance?.cutoff) {
        return null;
      }
      const date = parseISO(this.activeAwardInstance.cutoff);
      if (isValid(date)) {
        return date;
      }
      return null;
    },
    afterCutoff(): boolean {
      if (!this.cutoff) {
        return false;
      }
      return isPast(this.cutoff);
    },
    awardYear(): number {
      return (
        this.activeAwardInstance?.awardYear ||
        this.activeAward.settings.awardYear ||
        0
      );
    },
    awardYears(state): any {
      return state.activeAward?.instances
        .filter((instance) => !instance.isDraft)
        .map((instance) => ({
          year: instance.awardYear,
        }));
    },
    draftAwardInstance: (state) =>
      state.activeAward?.instances.find((instance) => instance.isDraft),
    latestAwardYear(): number | null {
      if (this.awardYears?.length) {
        return this.awardYears[0].year;
      }
      return null;
    },
    regions: (state) => {
      const latestInstance = state.activeAward?.instances[0];
      if (!latestInstance) {
        return null;
      }
      return latestInstance.regions;
    },
    stage(): any {
      const { activeAwardInstance } = this;
      if (!activeAwardInstance) {
        return null;
      }
      const nominationEndDate = parseISO(
        activeAwardInstance?.nominationEndDate ?? ""
      );
      if (isFuture(nominationEndDate)) {
        return {
          nomination: true,
        };
      }
      const votingStartDate = parseISO(
        activeAwardInstance.votingStartDate ?? ""
      );
      if (isFuture(votingStartDate)) {
        return {
          voterMarketing: true,
        };
      }
      const votingEndDate = parseISO(activeAwardInstance.votingEndDate ?? "");
      if (isFuture(votingEndDate)) {
        return {
          voting: true,
        };
      }
      const reviewStartDate = parseISO(
        activeAwardInstance.reviewPeriodStartDate ?? ""
      );
      if (isFuture(reviewStartDate)) {
        return {
          votingDone: true,
        };
      }
      const reviewEndDate = parseISO(
        activeAwardInstance.reviewPeriodEndDate ?? ""
      );
      if (isFuture(reviewEndDate)) {
        return {
          review: true,
        };
      }
      return {
        reviewDone: true,
      };
    },
    welcomeText: (state): string => {
      return state.activeAward?.settings.welcomeText || "Hi!";
    },
    awardsCeremonyOver(): boolean {
      const { awardsCeremonyNights = [] } = this.activeAwardInstance || {};
      const night = awardsCeremonyNights[awardsCeremonyNights.length - 1];
      if (!night) {
        return false;
      }
      return isPast(parseISO(night.date));
    },
    nominationEndDate(): Date | null {
      try {
        const endDate = parseISO(
          this.activeAwardInstance?.nominationEndDate ??
            this.activeAward.settings.nominationEndDate ??
            ""
        );
        if (isValid(endDate)) {
          // endDate = set(endDate, { hours: 23, minutes: 59, seconds: 59 });
          return endDate;
        }
        return null;
      } catch (error) {
        return null;
      }
    },
    pastNominationDeadline(): boolean {
      if (!this.nominationEndDate) {
        return false;
      }
      return isPast(this.nominationEndDate);
    },
  },
  actions: {
    async getLatestClientVersion(): Promise<string | null> {
      const response: { version: string } = await publicBoaHttp
        .get("awards/latest-client-version")
        .json();
      return semver.valid(response.version);
    },
    async createAwardInstance(instance: any) {
      const response = await privateBoaHttp
        .put("awards", { json: instance })
        .json();
      return response;
    },
    async getAnnouncements() {
      try {
        const response = await privateBoaHttp
          .get("awards/announcements?type=ANNOUNCEMENT")
          .json<{ success: boolean; items: Array<AnnouncementItem> }>();
        return response.items.map((item) => {
          return {
            ...item,
            date: parseISO(item.date as string),
            expiryDate: parseISO(item.expiryDate as string),
          };
        });
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    async getCalendarItems() {
      try {
        const response = await privateBoaHttp
          .get("awards/announcements?type=CALENDAR")
          .json<{ success: boolean; items: Array<CalendarItem> }>();
        return response.items.map((item) => {
          return {
            ...item,
            date: parseISO(item.date as string),
          };
        });
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    async getAwards() {
      this.awardsLoading = true;
      try {
        this.awards = await publicBoaHttp.get("awards").json();
        const { user } = useAuthStore();
        if (user && user.awardId && this.activeAward.id !== user.awardId) {
          this.setUserAward(user);
        } else {
          const award = this.awards.find(
            (a) => a.table === this.activeAward.table
          );
          if (!award) {
            return;
          }
          this.setActiveAward(award);
        }
        return this.awards;
      } catch (error) {
        console.error(error);
      } finally {
        this.awardsLoading = false;
      }
    },
    // async getAwards() {
    //   this.awardsLoading = true;
    //   try {
    //     this.awards = await trpcClient.getAwards.query();
    //     const { user } = useAuthStore();
    //     if (user && user.awardId && this.activeAward.id !== user.awardId) {
    //       this.setUserAward(user);
    //     } else {
    //       const award = this.awards.find(
    //         (a) => a.table === this.activeAward.table
    //       );
    //       if (!award) {
    //         return;
    //       }
    //       this.setActiveAward(award);
    //     }
    //     return this.awards;
    //   } catch (error) {
    //     console.error(error);
    //   } finally {
    //     this.awardsLoading = false;
    //   }
    // },
    setActiveAward(award: Award) {
      this.activeAward = award;
      try {
        const { faviconUrl, name } = this.activeAward.settings;
        useFavicon(faviconUrl);
        document.title = name ?? "Best of Awards";
      } catch (error) {
        console.error(error);
      }
    },
    async setUserAward(user: User) {
      const { awardId } = user;
      if (awardId) {
        const award = this.awards.find((a) => a.id === awardId);
        if (award) {
          this.setActiveAward(award);
        }
      }
    },
    async updateAwardInstance(instance: any) {
      const response = await privateBoaHttp
        .post(`awards/${instance.awardYear}`, {
          json: instance,
        })
        .json();
      return response;
    },
    async updateAwardSettings(settings: any) {
      try {
        const response = await privateBoaHttp
          .post("awards/settings", { json: settings })
          .json();
        return response;
      } catch (error) {
        console.error(error);
        return false;
      }
    },
    async updateAnnouncement(announcement: AnnouncementItem | CalendarItem) {
      await privateBoaHttp.post("awards/announcements", {
        json: { announcement },
      });
    },
    async createAnnouncement(announcement: AnnouncementItem | CalendarItem) {
      await privateBoaHttp.put("awards/announcements", {
        json: { announcements: [announcement] },
      });
    },
    async deleteAnnouncement(announcement: AnnouncementItem | CalendarItem) {
      await privateBoaHttp.delete("awards/announcements", {
        json: { announcement },
      });
    },
    publishFinalizedNominees(published: boolean) {
      return privateBoaHttp.post("awards/publish-finalized-nominees", {
        json: { published },
      });
    },
    publishFinalizedFinalists(published: boolean) {
      return privateBoaHttp.post("awards/publish-finalized-finalists", {
        json: { published },
      });
    },
    clear() {
      this.$reset();
    },
  },
});
